// src/components/Dashboard/SettingsMenu.tsx
import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import ReportService from '@services/ReportService';
import { authStorage } from '@/lib';

interface SettingsMenuProps {
  onClose: () => void;
  onOpenHelp?: () => void;
}

export const SettingsMenu: React.FC<SettingsMenuProps> = ({ onClose, onOpenHelp }) =>
{
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, GivenName, UserName } = useAuth();

  const profileImage =
    sessionStorage.getItem('C2_profileImage') || authStorage.getImageProfile() || '';
  const personName = sessionStorage.getItem('C2_personName') || GivenName() || UserName() || 'User';

  // Leer permisos de sessionStorage + localStorage fallback
  let isAdmin = false;
  let isPTOApprover = localStorage.getItem('C2_PTOApprover') === 'true';
  let isHRApprover = localStorage.getItem('C2_HRApprover') === 'true';
  try
  {
    const data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
    isAdmin = !!data.isAdmin;
    if (data.PTOApprover !== undefined) isPTOApprover = !!data.PTOApprover;
    if (data.HRApprover !== undefined) isHRApprover = !!data.HRApprover;
  }
  catch
  {
    /* ignore */
  }

  useEffect(() =>
  {
    const handleClickOutside = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleLogout = () =>
  {
    onClose();
    logout();
  };

  const handleNavigate = (path: string) =>
  {
    onClose();
    if (location.pathname === path)
    {
      navigate(`${path}?reload=${Date.now()}`);
    }
    else
    {
      navigate(path);
    }
  };

  const handleDownloadListEmployees = async () =>
  {
    onClose();
    try
    {
      const blob = await ReportService.GetListPeopleBlob();
      const date = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ListUsers_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
    catch
    {
      // silent — server errors are already logged in the service
    }
  };

  // profileImage ya viene como URL completa desde /profile/Get (ej: https://s3-us-west-2.amazonaws.com/corgee-dev/2/ProfileImages/xxx.png)
  const profileImageUrl = profileImage || '';

  const linkClass =
    'text-[10.5px] text-[#337ab7] dark:text-[#62accb] no-underline bg-transparent cursor-pointer leading-[10.5px] outline-none hover:text-[#23527c] dark:hover:text-[#a9a093] hover:underline focus:text-[#23527c] dark:focus:text-[#a9a093] focus:underline';

  const profileLinkClass =
    'block text-[10.5px] leading-[1.5] text-[#337ab7] dark:text-[#62accb] no-underline bg-transparent cursor-pointer pl-[5px] pb-[4px] -indent-[5px] outline-none hover:text-[#23527c] dark:hover:text-[#a9a093] hover:underline focus:text-[#23527c] dark:focus:text-[#a9a093] focus:underline';
  const sectionLabelClass = 'text-[10px] text-[#b0b0b1] dark:text-[#b6afa3]';

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white dark:bg-[#333535] border border-[#ccc] dark:border-[#4a4c4c] rounded shadow-md"
      style={{ top: '52px', right: '4px', width: '200px' }}
    >
      <ul className="list-none m-0 p-0 pl-[15px] border border-black/20 dark:border-white/10 shadow-[0_5px_10px_rgba(0,0,0,.2)] dark:shadow-[0_5px_10px_rgba(0,0,0,.5)] rounded-[6px]">
        {/* User Details Section */}
        <li className="border-b border-[#e5e5e5] dark:border-[#4a4c4c] px-[14px] py-[10px]">
          <div className={sectionLabelClass}>Logged in as</div>
          <div className="flex items-start gap-[8px] mt-[4px] mb-[8px]">
            <div
              className="w-[36px] h-[36px] rounded-full bg-gray-300 dark:bg-[#3a3c3c] bg-cover bg-center flex-shrink-0"
              style={profileImageUrl ? { backgroundImage: `url("${profileImageUrl}")` } : {}}
            />
            <div className="min-w-0">
              <div className="text-[12px] font-normal text-[#333] dark:text-[#aca396] leading-[16px]">
                {personName}
              </div>
              <a
                href="#"
                onClick={e =>
                {
                  e.preventDefault();
                  handleLogout();
                }}
                className="text-[11px] text-[#337ab7] dark:text-[#62accb] hover:text-[#23527c] dark:hover:text-[#a9a093]"
                style={{ position: 'absolute' }}
              >
                Logout
              </a>
            </div>
          </div>
          <div style={{ paddingTop: '20px' }}>
            <a
              href="#"
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/Profile');
              }}
              className={profileLinkClass}
            >
              My Profile
            </a>
          </div>
          <div>
            <a
              href="#"
              onClick={e =>
              {
                e.preventDefault();
                onOpenHelp?.();
                onClose();
              }}
              className={profileLinkClass}
            >
              Online Help
            </a>
          </div>
          {isAdmin && (
            <div>
              <a
                href="#"
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/AdminConsole');
                }}
                className={profileLinkClass}
              >
                Admin Console
              </a>
            </div>
          )}
        </li>

        {/* Time Tracking Section */}
        <li
          className="border-b border-[#e5e5e5] dark:border-[#4a4c4c] px-[14px] py-[10px]"
          style={{ fontSize: '.75em', lineHeight: '2em' }}
        >
          <div className={sectionLabelClass} style={{ height: '20px' }}>
            Time Tracking
          </div>
          <div style={{ height: '18px' }}>
            <a
              href="#"
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/Calendar');
              }}
              className={linkClass}
            >
              Submit Time Sheet
            </a>
          </div>
          <div style={{ height: '18px' }}>
            <a
              href="#"
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/TimeTrackingReport');
              }}
              className={linkClass}
            >
              Time Tracking Report
            </a>
          </div>
          <div style={{ height: '18px' }}>
            <a
              href="#"
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/AvailabilityReport');
              }}
              className={linkClass}
            >
              Availability Report
            </a>
          </div>
          {isAdmin && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/ETACostReport');
                }}
                className={linkClass}
              >
                ETA/Cost Report
              </a>
            </div>
          )}
          <div style={{ height: '18px' }}>
            <a
              href="#"
              className={linkClass}
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/CapturedTimeReport');
              }}
            >
              Captured Time Report
            </a>
          </div>
          <div style={{ height: '18px' }}>
            <a
              href="#"
              className={linkClass}
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/MonthlyPerformanceReport');
              }}
            >
              Performance Report
            </a>
          </div>
          {isPTOApprover && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/EfficiencyReport');
                }}
              >
                Efficiency Report
              </a>
            </div>
          )}
          {(isAdmin || isPTOApprover || isHRApprover) && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/AttendanceReport');
                }}
              >
                Attendance Report
              </a>
            </div>
          )}
          {isPTOApprover && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/ArtistReport');
                }}
              >
                Artist Report
              </a>
            </div>
          )}
        </li>

        {/* PTO Section */}
        <li className="border-b border-[#e5e5e5] dark:border-[#4a4c4c] px-[14px] py-[10px]">
          <div className={sectionLabelClass}>PTO</div>
          <div style={{ height: '18px' }}>
            <a
              href="#"
              className={linkClass}
              onClick={e =>
              {
                e.preventDefault();
                handleNavigate('/VacationsRequest');
              }}
            >
              PTO Request
            </a>
          </div>

          {(isHRApprover || isPTOApprover) && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/VacationsApproval');
                }}
              >
                PTO Approval
              </a>
            </div>
          )}
          {isHRApprover && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/PTOPermissions');
                }}
              >
                PTO Permissions
              </a>
            </div>
          )}
          {(isHRApprover || isPTOApprover) && (
            <div style={{ height: '18px' }}>
              <a
                href="#"
                className={linkClass}
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/PTOReport');
                }}
              >
                PTO Report
              </a>
            </div>
          )}
        </li>

        {/* Importing Tools Section - Admin only */}
        {isAdmin && (
          <li className="border-b border-[#e5e5e5] dark:border-[#4a4c4c] px-[14px] py-[10px]">
            <div className={sectionLabelClass}>Importing Tools</div>
            <div>
              <a
                href="#"
                onClick={e =>
                {
                  e.preventDefault();
                  handleNavigate('/ImportFromAsana');
                }}
                className={profileLinkClass}
              >
                Import From Asana
              </a>
            </div>
          </li>
        )}

        {/* People Section - Admin only */}
        {isAdmin && (
          <li className="px-[14px] py-[10px]">
            <div className={sectionLabelClass}>People</div>
            <div>
              <a
                href="#"
                onClick={e =>
                {
                  e.preventDefault();
                  handleDownloadListEmployees();
                }}
                className={profileLinkClass}
              >
                List employees
              </a>
            </div>
          </li>
        )}
      </ul>
    </div>,
    document.body,
  );
};
